
import {map} from 'rxjs/operators';
import { Injectable } from "@angular/core";
import { DiskService } from "../../services/disk/disk.service";
import { TimeOffService } from "../../services/time-off/time-off.service";
import { LogService } from "../../services/logging/log-service";
import { Endpoints } from "../../../config/endpoints.config";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { AuthenticationService } from "../../services/authentication.service";
import { firstValueFrom, Observable } from "rxjs";
import { TimeOffActivity, TimeOffStatus } from "../../classes/activity/timeoff.class";
import { FeatureActionsMap } from "../../classes/authentication/user.class";
import { DeviceService } from "../../services/device/device.service";
import { DeltaService, EntityNames, EntitySyncInfo } from "../delta/delta.service";
import { DB_KEY_PREFIXES } from "../../config/pouch-db.config";
import { ActivityType } from '@omni/classes/activity/activity.class';
import { EventsService } from '@omni/services/events/events.service';
import { TranslateService } from '@ngx-translate/core';
import { DateTimeFormatsService } from '@omni/services/date-time-formats/date-time-formats.service';

@Injectable({
  providedIn: 'root'
})
export class TimeOffDataService {

    private readonly DAYS_IN_FUTURE = 14;
    private readonly DAYS_PAST = 7;

    constructor(
        private disk: DiskService,
        private timeOffService: TimeOffService,
        private authenticationService: AuthenticationService,
        private logService: LogService,
        private http: HttpClient,
        private device: DeviceService,
        private deltaService: DeltaService,
        private event: EventsService,
        private translate: TranslateService,
        private readonly dateTimeFormatsService: DateTimeFormatsService,
    ) { }

    public async getTimeOffReason(loadFromDbOnly = false) {
        if (!this.authenticationService.hasFeatureAction(FeatureActionsMap.TIME_OFF_TOOL)) {
            return;
        }

        // Device is Offline, Fetch from the diskService and update the reasonsList
        if (loadFromDbOnly) {
                let offlineResponse = await this.disk.retrieve(DB_KEY_PREFIXES.TIMEOFF_REASONS);
                if(offlineResponse) this.timeOffService.mapTimeOffReasons(offlineResponse.raw);
            return;
        }

        // Device is online update diskService and update the reasonsList
        let url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.tot.TOT_REASON;
        let offlineFallback: boolean = false;
        const timeOffReasonSyncInfo: EntitySyncInfo = {
            entityName: EntityNames.timeOffReason,
            totalFailed: 0,
            totalSynced: 0,
            errors: [],
            syncStatus: true
        };
        try {
            let response = await this.http.get(url, Endpoints.GLOBAL_SYNC_HEADER).toPromise();
            this.timeOffService.mapTimeOffReasons(response);
            if (Array.isArray(response)) {
                timeOffReasonSyncInfo.totalSynced = response.length;
            }
            this.disk.updateOrInsert(DB_KEY_PREFIXES.TIMEOFF_REASONS, doc => ({ raw: response }))
                .catch((err)=> {
                    console.log("Error saving time-ff reasons to offline db!" , err);
                });
        } catch (httpError) {
            this.logService.logDebug(
                "Error fetching Time off reasons! Trying from DB",
                httpError
            );
            offlineFallback = true;

            this.deltaService.addSyncErrorToEntitySyncInfo(timeOffReasonSyncInfo, url, httpError);
        }

        this.deltaService.addEntitySyncInfo(timeOffReasonSyncInfo);
    }

    public async remapFromDisk() {
        if (!this.authenticationService.hasFeatureAction(FeatureActionsMap.TIME_OFF_TOOL)) {
            return;
        }
        const offlineResponse = await this.disk.retrieve(DB_KEY_PREFIXES.TIMEOFF_REASONS);
        if (offlineResponse) {
            await this.timeOffService.mapTimeOffReasons(offlineResponse.raw);
        }
    }

    public async getTimeOffFromActivities(raw: any, isOnline?: boolean) {
    }

    public async getUsersPositions(raw: object, saveToOffline:boolean = false) {
        if(raw && saveToOffline){
            this.disk.updateOrInsert(DB_KEY_PREFIXES.USER_POSITIONS, doc => ({raw: raw}))
                .catch((err)=> {
                    console.log("Error saving tuser positions data to offline db!" , err);
                });
        }
        if (raw["positions"]) {
            // mapping child positions to the authenticate service user object
            this.authenticationService.pushChildPositionsToCurrentUSer(raw['positions'])
            this.timeOffService.mapPositions(raw["positions"]);
        }
        if (raw["users"]) {
            this.timeOffService.mapUsers(raw["users"]);
        }
    }

    public createNewTimeOff(tot?: TimeOffActivity, offlineId?: string): Observable<any> {
        console.log(tot)
        const url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.tot.INITIATE_TOT;
        let payload = {
            indskr_starttime: tot === undefined ? this.getTimeStampInUserTimeZone("start") : tot.totStartTime.toDynamicsZonedTime(this.authenticationService.user.timezonecode).valueOf(),
            indskr_endtime: tot === undefined ? this.getTimeStampInUserTimeZone("end") : tot.totEndTime.toDynamicsZonedTime(this.authenticationService.user.timezonecode).valueOf(),
            indskr_name: this.authenticationService.hasFeatureAction(FeatureActionsMap.TIME_OFF_AUTOSUBJECT)? this.translate.instant('TIME_OFF') : this.translate.instant('TIME_OFF'),
            indskr_positionid: this.authenticationService.user.xPositionID,
            indskr_isalldayevent: tot === undefined ? true : tot.totIsAlldayEvent,
            indskr_local_timeoffrequestid: tot === undefined ? offlineId : tot.offlineTimeOffRequestId,
        };
        if(tot?.timeOffReason) {
          payload['indskr_reason'] = tot.timeOffReason;
          if(this.authenticationService.hasFeatureAction(FeatureActionsMap.TIME_OFF_AUTOSUBJECT)) {
            payload['indskr_name'] = tot.reason + ` - ${this.translate.instant('TIME_OFF')}`;
          }
        }
        let response = this.http.post(url, payload).pipe(map(res => {
            return res;
        }));
        console.log('Initiated New TimeOff request');
        return response;
    }

    public buildDefaultTimeOff(): TimeOffActivity {
        return new TimeOffActivity({
            indskr_timeoffrequestid: "",
            statecode: 0,
            statuscode: TimeOffStatus.Open,
            "statuscode@OData.Community.Display.V1.FormattedValue": "Open",
            "indskr_starttime@OData.Community.Display.V1.FormattedValue": this.getTimeStamp("start"),
            indskr_starttime: this.getTimeStamp("start"),
            _indskr_positionid_value: this.authenticationService.user.xPositionID,
            indskr_isalldayevent: true,
            "indskr_endtime@OData.Community.Display.V1.FormattedValue": this.getTimeStamp("end"),
            indskr_endtime: this.getTimeStamp("end"),
            indskr_reason: undefined,
            "indskr_reason@OData.Community.Display.V1.FormattedValue": "",
            indskr_name: "Time Off",
            _ownerid_value: this.authenticationService.user.systemUserID,
            "_ownerid_value@OData.Community.Display.V1.FormattedValue": "",
        }, ActivityType.TimeOffRequest);
    }

    public async createAndSaveTimeOff(tot: TimeOffActivity): Promise<{ savedOffline: boolean }> {
        tot.scheduledStart = new Date(tot.totStartTime);
        tot.scheduledEnd = new Date(tot.totEndTime);
        tot.scheduledStartLocale = tot.scheduledStart.toLocaleTimeString("en-US", { hour12: true, hour: "2-digit", minute: "2-digit" });
        tot.offlineTimeOffRequestId = `offline_tot_${new Date().getTime()}`;
        this.timeOffService.display = true;
        const created = this.device.isOffline ? null
            : await firstValueFrom(this.createNewTimeOff(tot)).then(raw => ({ raw }), () => null);
        await this.timeOffService.saveMyTimeOff(tot, !!created, created?.raw);
        return { savedOffline: !created };
    }

    public reOpenTimeOff(tot: TimeOffActivity): Observable<any> {
        const url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.tot.INITIATE_TOT;
        let payload = {
            "indskr_timeoffrequestid": tot.timeOffRequestId,
            "statecode": 0,
            "statuscode": 100000000,
            "indskr_positionid": tot.positionId,
            "indskr_isautoapproved": false,
        }
        try {
            let response = this.http.patch(url, payload).pipe(map(
                res => {
                    return res;
                },
                err => {
                    return err;
                }));
                console.log('Reoped timeoff');
                return response;
        } catch (error) {
            console.log(error);
        }
    }

    public updateTimeOff(tot: TimeOffActivity, isForReview: boolean, isAutoApproval: boolean): Observable<any> {
        const url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.tot.INITIATE_TOT;
        let payload = {
            "indskr_starttime": new Date(tot.totStartTime).toDynamicsZonedTime(this.authenticationService.user.timezonecode).getTime(),
            "indskr_endtime": new Date(tot.totEndTime).toDynamicsZonedTime(this.authenticationService.user.timezonecode).getTime(),
            "indskr_reason": tot.timeOffReason,
            "indskr_name": tot.name,
            "indskr_positionid": tot.positionId,
            "indskr_isalldayevent": tot.totIsAlldayEvent,
            "indskr_timeoffrequestid": tot.timeOffRequestId,
            "statecode": 0,
            "statuscode": tot.statuscode,
            "indskr_comments": tot.comments
        };
        if (tot.indskr_eventlength) {
          payload['indskr_eventlength'] = tot.indskr_eventlength;
        }
        if (isForReview && !isAutoApproval && tot.statuscode === TimeOffStatus.Open) {
            payload.statuscode = TimeOffStatus.InReview;
            payload.statecode = 1;
            payload['indskr_isautoapproved'] = false;
        } else if (isAutoApproval && tot.statuscode === TimeOffStatus.Open) {
            payload.statuscode = TimeOffStatus.Approved;
            payload.statecode = 1;
            payload['indskr_approver'] = this.authenticationService.user.xSystemUserID;
            payload['indskr_isautoapproved'] = true;
            payload['indskr_approvaldate'] = new Date().valueOf();
            console.log('Submitted TimeOff request for APPROVAL ');
        }
        try {
            let response = this.http.patch(url, payload).pipe(map(
                res => {
                    return res;
                },
                err => {
                    return err;
                }));
                console.log('Submitted TimeOff request for review');
                return response;
        } catch (error) {
            console.log(error);
        }


    }

    public cancelReviewTimeOff(tot: TimeOffActivity): Observable<any> {
        const url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.tot.INITIATE_TOT;
        let payload = {
            "indskr_starttime": new Date(tot.totStartTime).toDynamicsZonedTime(this.authenticationService.user.timezonecode).getTime(),
            "indskr_endtime": new Date(tot.totEndTime).toDynamicsZonedTime(this.authenticationService.user.timezonecode).getTime(),
            "indskr_reason": tot.timeOffReason,
            "indskr_name": tot.name,
            "indskr_positionid": tot.positionId,
            "indskr_isalldayevent": tot.totIsAlldayEvent,
            "indskr_timeoffrequestid": tot.timeOffRequestId,
            "statecode": 0,
            "statuscode": TimeOffStatus.Open
        };

        try {
            let response = this.http.patch(url, payload).pipe(map(
                res => {
                    return res;
                },
                err => {
                    return err;
                }));
                console.log('Submitted TimeOff request for review');
                return response;
        } catch (error) {
            console.log(error);
        }


    }

    public approveTimeOff(tot: TimeOffActivity, approve: boolean): Observable<any> {
        const url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.tot.INITIATE_TOT;

        let payload = {
            "indskr_starttime": new Date(tot.totStartTime).toDynamicsZonedTime(this.authenticationService.user.timezonecode).getTime(),
            "indskr_endtime": new Date(tot.totEndTime).toDynamicsZonedTime(this.authenticationService.user.timezonecode).getTime(),
            "indskr_reason": tot.timeOffReason,
            "indskr_name": tot.name,
            "indskr_positionid": tot.positionId,
            "indskr_isalldayevent": tot.totIsAlldayEvent,
            "indskr_timeoffrequestid": tot.timeOffRequestId,
            "statecode": 1,
            "statuscode": TimeOffStatus.Approved,
            "indskr_approvercomments": tot.approverComments
        };
        if (!approve) {
            payload.statuscode = TimeOffStatus.Open;
            payload.statecode = 0;
        } else {
            payload['indskr_approver'] = this.authenticationService.user.xSystemUserID;
            payload['indskr_isautoapproved'] = false;
            payload['indskr_approvaldate'] = new Date().valueOf();
        }
        let response = this.http.patch(url, payload).pipe(map(
            res => {
                return res;
            },
            err => {
                return err;
            }));
        return response;
    }

    public async deleteTimeOff(timeOffRequestID): Promise<any> {
        let url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.tot.DELETE_TOT.replace('{timeOffRequestID}', timeOffRequestID);

        let response = await this.http.delete(url).toPromise();
        console.log('Initiated Delete TimeOff request');
        return response;
    }

    public getTimeStamp(type: string, tot?: TimeOffActivity): number {
      const baseDate = tot?.totStartTime || new Date();
      const meridian = this.dateTimeFormatsService.formatDate(new Date(), 'A');
      
      const timeConfigs: Record<string, { hour: number; minute: number; second: number }> = {
        start: { hour: 0, minute: 0, second: 0 },
        end: { hour: 23, minute: 59, second: 59 },
        halfStart: meridian === 'PM' && tot ? { hour: 12, minute: 0, second: 0 } : { hour: 0, minute: 0, second: 0 },
        halfEnd: meridian === 'PM' ? { hour: 23, minute: 59, second: 59 } : { hour: 11, minute: 59, second: 59 },
        otherStart: { hour: 8, minute: 0, second: 0 },
        otherEnd: { hour: 9, minute: 0, second: 0 },
        none: { hour: 12, minute: 0, second: 0 },
      };
    
      if (type === 'current') {
        return new Date(baseDate).getTime();
      }
    
      const config = timeConfigs[type] || timeConfigs.start;
      const d = new Date(baseDate);
      d.setHours(config.hour, config.minute, config.second, 0);
      return d.valueOf();
    }

    private getTimeStampInUserTimeZone(type: string, tot?: TimeOffActivity) {
      return new Date(this.getTimeStamp(type, tot)).toDynamicsZonedTime(this.authenticationService.user.timezonecode).valueOf();
    }

    async createNewTimeOffFromOfflineData(tot?: TimeOffActivity): Promise<any> {
        const url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.tot.INITIATE_TOT;
        let payload = {
            indskr_starttime: tot.totStartTime.toDynamicsZonedTime(this.authenticationService.user.timezonecode).valueOf(),
            indskr_endtime: tot.totEndTime.toDynamicsZonedTime(this.authenticationService.user.timezonecode).valueOf(),
            indskr_name: tot.name,
            indskr_positionid: this.authenticationService.user.xPositionID,
            indskr_reason: tot.timeOffReason,
            indskr_isalldayevent: tot.totIsAlldayEvent,
            indskr_local_timeoffrequestid: tot.offlineTimeOffRequestId,
        };

        let response: any;
        try {
            response = await this.http.post(url, payload).toPromise();
        } catch (error) {
            console.error('createNewTimeOffFromOfflineData: ', error);
            return Promise.reject(error);
        }

        if (response) {
            this.timeOffService.saveMyTimeOff(tot, true, response);
            this.timeOffService.setSelectedTot(tot);
            this.timeOffService.deleteTimeOffFromOfflineDoc(tot);
        }
    }

    async uploadOneOfflineTimeOff(tot: TimeOffActivity): Promise<any> {
        if (this.device.isOffline) return;

        let offlineTimeOffs = await this.disk.loadOfflineTimeOffs();

        if (!offlineTimeOffs || !offlineTimeOffs['myTos'] || !Array.isArray(offlineTimeOffs['myTos']) || offlineTimeOffs['myTos'].length === 0) return;

        const dataIdx = offlineTimeOffs['myTos'].findIndex(t => t.indskr_local_timeoffrequestid === tot.offlineTimeOffRequestId);

        if (dataIdx < 0) {
            console.warn(`uploadOneOfflineTimeOff: Coulnd't find ID ${tot.offlineTimeOffRequestId} from offline data`);
            return;
        }

        let payload = {
            indskr_starttime: tot.totStartTime.toDynamicsZonedTime(this.authenticationService.user.timezonecode).valueOf(),
            indskr_endtime: tot.totEndTime.toDynamicsZonedTime(this.authenticationService.user.timezonecode).valueOf(),
            indskr_name: tot.name,
            indskr_positionid: this.authenticationService.user.xPositionID,
            indskr_reason: tot.timeOffReason,
            indskr_isalldayevent: tot.totIsAlldayEvent,
            indskr_local_timeoffrequestid: tot.offlineTimeOffRequestId,
        };

        let response: any;
        try {
            const url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.tot.INITIATE_TOT;
            response = await this.http.post(url, payload).toPromise();
        } catch (error) {
            console.error('uploadOneOfflineTimeOff: ', error);
            return Promise.reject('');
        }

        if (response) {
            tot.pendingPushToDynamics = false;
            // Save to db
            await this.timeOffService.saveMyTimeOff(tot, true, response);
            // Remove from offline doc
            offlineTimeOffs['myTos'].splice(dataIdx, 1);
            await this.disk.updateDocWithIdAndRev(offlineTimeOffs);
        } else {
            console.error('uploadOneOfflineTimeOff: It seems request succeeded but there was no response from server.');
            return Promise.reject('');
        }
    }

    public async getTeamTimeOffOnline():Promise<any>{
      let headers = new HttpHeaders();
      headers = headers.set('Sync-Service', 'true');
      headers = headers.set('X-SystemUserId', this.authenticationService.user.xSystemUserID);
      const positions = this.authenticationService.user.positions.map((o) => {
        return o.ID
      })
      let url: string = this.authenticationService.userConfig.activeInstance.entryPointUrl + Endpoints.activites.GET_TEAM_ACTIVITIES_FOR_TOOLS_BY_ACTIVITY_TYPES;
      const dataRange = this.authenticationService.getFromToDateRangeInUTCMiliSec(undefined);
      url = url.replace('{startDate}', dataRange.from);
      url = url.replace('{endDate}', dataRange.to);
      url = url.replace('{teamActivity}', 'timeOff');
      url = url.replace('{positionIDs}', positions.toString());
      try {
        const response =  await this.http.get(url, { headers }).toPromise();
        if(response && response['teamTimeOffs'] && Array.isArray(response['teamTimeOffs'])){
          this.timeOffService.teamTot = [];
          response['teamTimeOffs'].forEach(rawTeamTimeOff=> {
            if (rawTeamTimeOff['indskr_starttime'] && !isNaN(rawTeamTimeOff['indskr_starttime'])) {
              rawTeamTimeOff['indskr_starttime'] = new Date(parseFloat(rawTeamTimeOff['indskr_starttime'])).fromDynamicsZonedTime(this.authenticationService.user.timezonecode).valueOf();
            }
            if (rawTeamTimeOff['indskr_endtime'] && !isNaN(rawTeamTimeOff['indskr_endtime'])) {
              rawTeamTimeOff['indskr_endtime'] = new Date(parseFloat(rawTeamTimeOff['indskr_endtime'])).fromDynamicsZonedTime(this.authenticationService.user.timezonecode).valueOf();
            }

            /* Pouch db doesnt allow to store keyword starting with _ */
            const teamTimeOff = new TimeOffActivity(rawTeamTimeOff, ActivityType.TimeOff);
            this.timeOffService.teamTot.push(teamTimeOff);
          })
        }
      } catch (error) {
        console.log('Fetch Team Cases for Tool Error: '+error)
      }
      return;
    }
}
